/**
 * @file: Navbar.jsx
 * @status: Sovereign Navbar (V12.6.4) - ULTRA-COMPACT & NO-CONGESTION
 * @authority: @CEO - Abdullah Ahmed Al-Zahrani
 * @version: 2026.04.27
 * @description: حل مشكلة التزاحم؛ تحويل الروابط لأيقونات ذكية؛ دمج زر اللغة في كبسولة الأدوات بختم @CEO.
 */

import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  LayoutGrid, Wallet, Factory, Box, 
  UserCheck, ShieldCheck, Bell, LogOut, Globe, FileText, AlertCircle 
} from 'lucide-react';
import { message, Tooltip, Modal, Badge } from 'antd';

import logo2026 from '../assets/logo2026.png'; 

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

  const userRole = localStorage.getItem('huger_user_role') || 'Staff';
  const isAuthenticated = localStorage.getItem('huger_authenticated') === 'true';

  const toggleLanguage = () => {
    const newLang = i18n.language === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(newLang);
    message.success(newLang === 'ar' ? "تم تفعيل الواجهة العربية" : "English Interface Activated");
  };

  const handleLogout = () => {
    const hide = message.loading("جاري تأمين الخروج...", 0);
    localStorage.clear();
    setTimeout(() => {
      hide();
      message.success("تم إغلاق الجلسة السيادية بنجاح ✅");
      navigate('/', { replace: true });
      window.location.reload();
    }, 800);
  };

  const navItems = [
    { path: '/dashboard', label: t('dashboard_title') || 'الرادار', icon: <LayoutGrid size={18} />, roles: ['CEO', 'Manager', 'Investor', 'Staff'] },
    { path: '/control-center', label: 'إدارة الهويات', icon: <UserCheck size={18} />, roles: ['CEO'] },
    { path: '/invoices', label: t('invoices_hub') || 'الفواتير', icon: <FileText size={18} />, roles: ['CEO', 'Manager'] },
    { path: '/financing', label: t('finance_radar') || 'التمويل', icon: <Wallet size={18} />, roles: ['CEO', 'Manager'] },
    { path: '/production', label: t('production_radar') || 'الإنتاج', icon: <Factory size={18} />, roles: ['CEO', 'Manager', 'Staff'] },
    { path: '/inventory', label: t('innovation_depot') || 'المخزون', icon: <Box size={18} />, roles: ['CEO', 'Manager'] },
  ];

  const filteredNavItems = navItems.filter(item => item.roles.includes(userRole));

  if (!isAuthenticated) return null;

  return (
    <nav className="bg-[#050B14]/95 border-b border-white/5 px-4 md:px-6 py-2 flex justify-between items-center w-full sticky top-0 z-[1000] backdrop-blur-3xl shadow-2xl" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
      
      {/* 1. شعار ضخامة - نسخة رشيقة */}
      <div className="flex items-center gap-4 flex-shrink-0">
        <Link to="/" id="nav_logo_home" className="flex items-center gap-2 group">
          <img src={logo2026} alt="HUGER" className="h-7 w-auto transition-all group-hover:scale-105" />
          <span className="text-white font-black text-xs italic uppercase tracking-tighter hidden sm:block">HUGER</span>
        </Link>
        <div className="w-[1px] h-4 bg-white/10 hidden lg:block mx-2"></div>
      </div>

      {/* 2. روابط الملاحة: أيقونات فقط لتوفير المساحة */}
      <div className="flex items-center gap-1 bg-white/5 p-1 rounded-xl border border-white/5 mx-2">
        {filteredNavItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Tooltip key={item.path} title={item.label} placement="bottom">
              <Link 
                id={`nav_link_${item.path.replace('/', '')}`}
                to={item.path}
                className={`p-2.5 rounded-lg transition-all duration-300 relative group ${isActive ? 'bg-[#F57C00] text-white shadow-lg' : 'text-slate-400 hover:text-white hover:bg-white/10'}`}
              >
                <span className={`transition-transform duration-300 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`}>
                  {item.icon}
                </span>
                {isActive && <div className="absolute -bottom-[2px] left-1/2 -translate-x-1/2 w-1 h-1 bg-white rounded-full"></div>}
              </Link>
            </Tooltip>
          );
        })}
      </div>

      {/* 3. كبسولة الأدوات السيادية: مدمجة بالكامل */}
      <div className="flex items-center gap-2 flex-shrink-0">
        
        <div className="flex items-center gap-1.5 bg-black/40 p-1 rounded-xl border border-white/10 shadow-inner">
          
          {/* زر اللغة المدمج */}
          <button 
            id="btn_nav_lang_toggle"
            name="language_toggle"
            onClick={toggleLanguage}
            className="flex items-center justify-center p-2 text-slate-400 hover:text-[#F57C00] hover:bg-white/5 rounded-lg transition-all border-none cursor-pointer"
          >
            <Globe size={16} />
            <span className="text-[8px] font-black ml-1 uppercase hidden xl:block">
              {i18n.language === 'ar' ? 'EN' : 'AR'}
            </span>
          </button>

          <div className="w-[1px] h-3 bg-white/10 mx-0.5"></div>

          {/* الرتبة */}
          <div className="px-2 hidden md:block">
            <span className="text-[9px] text-[#F57C00] font-black italic uppercase tracking-tighter">@{userRole}</span>
          </div>

          <div className="w-[1px] h-3 bg-white/10 mx-0.5"></div>
          
          {/* التنبيهات */}
          <div 
            id="btn_nav_alerts" 
            className="p-2 text-slate-500 hover:text-[#F57C00] cursor-pointer transition-colors" 
            onClick={() => setIsNotificationsOpen(true)}
          >
            <Badge dot color="#F57C00" offset={[-1, 1]} size="small">
              <Bell size={18} />
            </Badge>
          </div>

          {/* تسجيل الخروج */}
          <button 
            id="btn_nav_logout_sovereign"
            name="logout_action"
            onClick={handleLogout} 
            className="p-2 text-red-500/70 hover:text-white hover:bg-red-500 rounded-lg transition-all border-none cursor-pointer"
          >
            <LogOut size={18} />
          </button>
        </div>
      </div>

      {/* مودال التنبيهات */}
      <Modal
        title={<div className="text-white font-black uppercase italic tracking-tighter flex items-center gap-2"><AlertCircle size={18} className="text-[#F57C00]"/> Sovereign Alerts</div>}
        open={isNotificationsOpen}
        onCancel={() => setIsNotificationsOpen(false)}
        footer={null}
        centered
        width={380}
        styles={{ body: { backgroundColor: '#050B14', padding: '24px' } }}
      >
        <div className="space-y-4" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
          <div className="p-4 bg-white/5 border border-[#F57C00]/20 rounded-[1.5rem] shadow-xl">
            <p className="text-[#F57C00] font-black text-[11px] mb-2 uppercase italic">UI Optimization V12.6.4</p>
            <p className="text-slate-400 text-[11px] leading-relaxed font-bold">سعادة الرئيس، تم ضغط واجهة الملاحة لتناسب رؤية 100% دون الحاجة للتصغير. الأيقونات الآن ذكية وتفاعلية.</p>
          </div>
          <button className="w-full py-3 bg-white/5 rounded-xl text-slate-500 text-[10px] font-black uppercase tracking-[0.2em] hover:text-[#F57C00] transition-colors border-none cursor-pointer" onClick={() => setIsNotificationsOpen(false)}>
            Close
          </button>
        </div>
      </Modal>

      <style>{`
        .ant-modal-content { background-color: #050B14 !important; border: 1px solid rgba(245,124,0,0.3) !important; border-radius: 2rem !important; }
        .ant-modal-header { background-color: #050B14 !important; border-bottom: 1px solid rgba(255,255,255,0.05) !important; }
        .ant-tooltip-inner { background-color: #F57C00 !important; font-weight: 900 !important; font-size: 10px !important; text-transform: uppercase !important; border-radius: 8px !important; }
      `}</style>
    </nav>
  );
};

export default Navbar;